import React, { useEffect, useRef, useState } from 'react';
import Layout from '../components/Layout';
import { api } from '../api';

const initialForm = {
  eventName: '',
  heading: '',
  message: '',
  brandName: 'Shahi Raj',
  mobileNumber: '',
  footer2: '',
  visualStyle: '',
  customStyle: '',
  theme: 'Light',
  format: '4:5',
  script: 'auto',
  quality: 'medium',
  count: 1,
};

export default function GreetingsCreative() {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [creatives, setCreatives] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsBusy, setSuggestionsBusy] = useState(false);
  const [suggestionError, setSuggestionError] = useState('');
  const [suggestionNotice, setSuggestionNotice] = useState('');
  const [job, setJob] = useState(null);
  const pollTimer = useRef(null);

  useEffect(() => () => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
  }, []);

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSuggestText = async () => {
    setSuggestionError('');
    setSuggestionNotice('');
    setSuggestions([]);

    if (!form.eventName.trim()) {
      setSuggestionError('Enter the event name first.');
      return;
    }

    setSuggestionsBusy(true);

    try {
      const result = await api.suggestGreetingText({
        eventName: form.eventName,
        visualStyle: form.visualStyle,
        customStyle: form.customStyle,
        script: form.script,
      });

      if (!Array.isArray(result.suggestions) || !result.suggestions.length) {
        throw new Error('No suggestions were returned. Please try again.');
      }

      setSuggestions(result.suggestions);
    } catch (err) {
      setSuggestionError(err.message || 'Could not suggest greeting text.');
    } finally {
      setSuggestionsBusy(false);
    }
  };

  const copySuggestionText = async (text, label) => {
    setSuggestionError('');
    setSuggestionNotice('');

    try {
      await navigator.clipboard.writeText(text);
      setSuggestionNotice(`${label} copied.`);
    } catch {
      setSuggestionError('Clipboard access failed. Please copy the text manually.');
    }
  };

  const applySuggestion = (suggestion) => {
    setForm((current) => ({
      ...current,
      heading: suggestion.heading,
      message: suggestion.message,
    }));
    setSuggestionNotice('Heading and supporting message filled in.');
    setSuggestionError('');
  };

  const pollGreetingJob = (jobId, total, failures) => {
    pollTimer.current = setTimeout(async () => {
      try {
        const result = await api.getGreetingVariants(jobId);
        const current = result?.job;
        if (!current) throw new Error('Job status was empty.');

        setJob(current);
        setCreatives(
          [...(current.creatives || [])].sort((a, b) => (a.variant || 0) - (b.variant || 0))
        );

        if (current.status === 'running') {
          setStatus(`Generated ${(current.creatives || []).length} of ${total}...`);
          pollGreetingJob(jobId, total, 0);
          return;
        }

        if (current.status === 'failed') {
          setError(current.error || 'Greeting generation failed.');
          setStatus('');
        } else {
          const done = (current.creatives || []).length;
          const failed = (current.errors || []).length;
          setStatus(
            failed
              ? `${done} of ${total} creatives generated; ${failed} failed: ${String((current.errors[0] || {}).error || '').slice(0, 220)}`
              : `${done} greeting creative${done === 1 ? '' : 's'} generated successfully.`
          );
        }
        setBusy(false);
      } catch (err) {
        if (/not found/i.test(err.message || '')) {
          setError('The generation job was lost (the server restarted). Please generate again.');
          setStatus('');
          setBusy(false);
        } else if (failures < 5) {
          pollGreetingJob(jobId, total, failures + 1);
        } else {
          setError(err.message || 'Lost connection while generating.');
          setStatus('');
          setBusy(false);
        }
      }
    }, 3000);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setStatus('');
    setCreatives([]);

    if (!form.eventName.trim() || !form.heading.trim()) {
      setError('Enter both the event name and main greeting heading.');
      return;
    }

    setBusy(true);
    setJob(null);
    const total = Number(form.count) || 1;
    setStatus(`Generating ${total} greeting creative${total === 1 ? '' : 's'}...`);

    try {
      const started = await api.startGreetingVariants({ ...form, count: total });
      const jobId = started?.job?.id;
      if (!jobId) throw new Error('The server did not start the generation job.');
      setJob(started.job);
      pollGreetingJob(jobId, total, 0);
    } catch (err) {
      setError(err.message || 'Could not generate greeting creative.');
      setStatus('');
      setBusy(false);
    }
  };

  const fieldClass =
    'mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-100';

  const labelClass = 'block text-sm font-medium text-gray-700';

  return (
    <Layout>
      <div className="mx-auto max-w-5xl space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Greeting Creative
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Create greeting cards and event creatives for social media.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
        >
          <section className="space-y-4">
            <h2 className="text-base font-semibold text-gray-900">
              Greeting content
            </h2>

            <div>
              <label className={labelClass} htmlFor="eventName">
                Event name *
              </label>
              <input
                id="eventName"
                className={fieldClass}
                value={form.eventName}
                onChange={(e) => update('eventName', e.target.value)}
                placeholder="e.g. Diwali, Eid, Ganesh Chaturthi, Birthday"
                required
              />
            </div>

            <div className="space-y-2">
              <button
                type="button"
                onClick={handleSuggestText}
                disabled={suggestionsBusy || busy}
                className="rounded-lg border border-purple-300 bg-purple-50 px-4 py-2 text-sm font-medium text-purple-800 hover:bg-purple-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {suggestionsBusy ? 'Suggesting text...' : 'Suggest Greeting Text'}
              </button>
              <p className="text-xs text-gray-500">
                Get five heading and message ideas. Your existing text will not change unless you choose an action below.
              </p>

              {suggestionError && (
                <p className="text-sm text-red-600" role="alert">
                  {suggestionError}
                </p>
              )}

              {suggestionNotice && (
                <p className="text-sm text-green-700" role="status">
                  {suggestionNotice}
                </p>
              )}

              {suggestions.length > 0 && (
                <div className="space-y-3 pt-2">
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={`${index}-${suggestion.heading}`}
                      className="space-y-3 rounded-lg border border-gray-200 p-4"
                    >
                      <div className="text-xs font-semibold uppercase tracking-wide text-purple-700">
                        Option {index + 1}{suggestion.tone ? ` · ${suggestion.tone}` : ''}
                      </div>

                      <div>
                        <div className="mb-1 text-xs font-medium text-gray-500">
                          Heading
                        </div>
                        <p className="whitespace-pre-wrap text-sm font-semibold text-gray-900">
                          {suggestion.heading}
                        </p>
                        <button
                          type="button"
                          onClick={() => copySuggestionText(suggestion.heading, 'Heading')}
                          className="mt-2 rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          Copy Heading
                        </button>
                      </div>

                      <div>
                        <div className="mb-1 text-xs font-medium text-gray-500">
                          Supporting message
                        </div>
                        <p className="whitespace-pre-wrap text-sm text-gray-700">
                          {suggestion.message}
                        </p>
                        <button
                          type="button"
                          onClick={() => copySuggestionText(suggestion.message, 'Supporting message')}
                          className="mt-2 rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          Copy Supporting Message
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={() => applySuggestion(suggestion)}
                        className="rounded-lg bg-purple-700 px-4 py-2 text-sm font-medium text-white hover:bg-purple-800"
                      >
                        Use Both
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className={labelClass} htmlFor="heading">
                Main heading *
              </label>
              <input
                id="heading"
                className={fieldClass}
                value={form.heading}
                onChange={(e) => update('heading', e.target.value)}
                placeholder="The main greeting text"
                required
              />
            </div>

            <div>
              <label className={labelClass} htmlFor="message">
                Supporting message (optional)
              </label>
              <textarea
                id="message"
                rows={3}
                className={fieldClass}
                value={form.message}
                onChange={(e) => update('message', e.target.value)}
                placeholder="Add a blessing, wish, or short message"
              />
            </div>
          </section>

          <section className="space-y-4 border-t border-gray-100 pt-5">
            <h2 className="text-base font-semibold text-gray-900">
              Branding
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass} htmlFor="brandName">
                  Footer / brand name
                </label>
                <input
                  id="brandName"
                  className={fieldClass}
                  value={form.brandName}
                  onChange={(e) => update('brandName', e.target.value)}
                  placeholder="Leave blank for no branding"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Shahi Raj uses the existing logo and shahiraj.com. Other
                  names use text branding only.
                </p>
              </div>

              <div>
                <label className={labelClass} htmlFor="mobileNumber">
                  Mobile number (optional)
                </label>
                <input
                  id="mobileNumber"
                  type="tel"
                  className={fieldClass}
                  value={form.mobileNumber}
                  onChange={(e) => update('mobileNumber', e.target.value)}
                  placeholder="Contact number"
                />
              </div>

              <div className="sm:col-span-2">
                <label className={labelClass} htmlFor="footer2">
                  Footer Line 2 (optional)
                </label>
                <input
                  id="footer2"
                  className={fieldClass}
                  value={form.footer2}
                  onChange={(e) => update('footer2', e.target.value)}
                  placeholder="A closing wish, blessing, or second footer line"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Appears separately below the logo and contact details.
                </p>
              </div>
            </div>
          </section>

          <section className="space-y-4 border-t border-gray-100 pt-5">
            <h2 className="text-base font-semibold text-gray-900">
              Design settings
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={labelClass} htmlFor="customStyle">
                  Visual brief (optional)
                </label>
                <textarea
                  id="customStyle"
                  rows={3}
                  className={fieldClass}
                  value={form.customStyle}
                  onChange={(e) => update('customStyle', e.target.value)}
                  placeholder="Describe the visual direction you want"
                />
              </div>

              <div>
                <label className={labelClass} htmlFor="quality">
                  Image quality
                </label>
                <select
                  id="quality"
                  className={fieldClass}
                  value={form.quality}
                  onChange={(e) => update('quality', e.target.value)}
                >
                  <option value="low">Low — fastest, lowest cost</option>
                  <option value="medium">Medium — recommended</option>
                  <option value="high">High — best detail, slowest</option>
                </select>
              </div>

              <div>
                <label className={labelClass} htmlFor="count">
                  Number of variants
                </label>
                <select
                  id="count"
                  className={fieldClass}
                  value={form.count}
                  onChange={(e) => update('count', Number(e.target.value))}
                >
                  <option value={1}>1 — single creative</option>
                  <option value={2}>2 variants</option>
                  <option value={3}>3 variants</option>
                  <option value={4}>4 variants</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Each variant is a separate image with its own background and fonts, so cost and time grow with the count.
                </p>
              </div>

              <div>
                <label className={labelClass} htmlFor="format">
                  Image format
                </label>
                <select
                  id="format"
                  className={fieldClass}
                  value={form.format}
                  onChange={(e) => update('format', e.target.value)}
                >
                  <option value="4:5">4:5 — Social post</option>
                  <option value="1:1">1:1 — Square</option>
                  <option value="9:16">9:16 — Story / status</option>
                </select>
              </div>

              <div>
                <label className={labelClass} htmlFor="script">
                  Language / script
                </label>
                <select
                  id="script"
                  className={fieldClass}
                  value={form.script}
                  onChange={(e) => update('script', e.target.value)}
                >
                  <option value="auto">Detect automatically</option>
                  <option value="latin">Latin / English</option>
                  <option value="devanagari">Devanagari — Hindi / Marathi</option>
                  <option value="alkanz">Lisan ud Dawat — AlKanz font</option>
                </select>
              </div>
            </div>
          </section>

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          {status && (
            <div
              role="status"
              className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"
            >
              {status}
            </div>
          )}

          {(creatives.length > 0 || busy) && (
            <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-5">
              <h2 className="text-lg font-semibold text-gray-900">
                Generated greeting creatives
              </h2>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {creatives.map((creative, index) => (
                  <article
                    key={creative.filename || creative.image_url || index}
                    className="overflow-hidden rounded-lg border border-gray-200"
                  >
                    <img
                      src={creative.image_url}
                      alt={`${form.eventName} greeting creative`}
                      className="block h-auto w-full"
                      loading="lazy"
                    />
                    <div className="p-3">
                      <p className="mb-2 break-words text-xs text-gray-500">
                        {creative.filename}
                      </p>
                      <a
                        href={creative.image_url}
                        download={creative.filename || true}
                        className="inline-flex rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700"
                      >
                        Download PNG
                      </a>
                    </div>
                  </article>
                ))}
                {busy && Array.from({
                  length: Math.max(
                    0,
                    (job?.requested_count || Number(form.count) || 1) -
                      creatives.length - (job?.errors?.length || 0)
                  ),
                }).map((_, i) => (
                  <div
                    key={`pending-${i}`}
                    style={{ aspectRatio: form.format.replace(':', ' / ') }}
                    className="flex animate-pulse items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 text-xs text-gray-500"
                  >
                    Generating...
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-5">
            <p className="text-xs text-gray-500">
              Creates one PNG greeting creative in the existing creative gallery.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={busy}
                className="rounded-lg bg-purple-700 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? 'Generating...' : Number(form.count) > 1 ? `Generate ${form.count} Variants` : 'Generate Greeting'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
}
